'use client';

import { ChevronsUpDown, LogOutIcon, MegaphoneIcon, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLogout } from '@r/api/auth.tsx';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Avatar, AvatarFallback, AvatarImage } from '@ui/avatar.tsx';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@r/components/ui/dropdown-menu.tsx';

import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from '@r/components/ui/sidebar.tsx';
import { queryClient } from '@r/utils/utils.ts';
import { useCallback } from 'react';

export function NavUser({
  user
}: {
  user: {
    name: string;
    email: string;
    avatar: string | null;
  };
}) {
  const { isMobile } = useSidebar();
  const { t } = useTranslation();
  const logout = useLogout();
  const router = useRouter();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    queryClient.invalidateQueries();
    router.invalidate().finally(() => {
      navigate({ to: '/login' });
    });
  };

  const handleGoToProfile = useCallback(() => {
    navigate({ to: '/my-account' });
  }, [navigate]);

  const handleGoToChangelogs = useCallback(() => {
    navigate({ to: '/changelogs' });
  }, [navigate]);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="w-8 h-8 rounded-lg">
                {user.avatar ? (
                  <AvatarImage src={user.avatar} alt={user.name} />
                ) : (
                  <AvatarFallback
                    text={`${user.name}`}
                    initials={true}
                    className="rounded-lg"
                    fontSize="nav"
                  />
                )}
              </Avatar>
              <div className="grid flex-1 text-sm leading-tight text-left">
                <span className="font-semibold truncate">{user.name}</span>
                <span className="text-xs truncate">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="w-8 h-8 rounded-lg">
                  {user.avatar ? (
                    <AvatarImage src={user.avatar} alt={user.name} />
                  ) : (
                    <AvatarFallback
                      text={`${user.name}`}
                      initials={true}
                      className="rounded-lg"
                      fontSize="nav"
                    />
                  )}
                </Avatar>

                <div className="grid flex-1 text-sm leading-tight text-left">
                  <span className="font-semibold truncate">{user.name}</span>
                  <span className="text-xs truncate">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleGoToProfile} className="cursor-pointer group">
              <p className="flex items-center child">
                <User className="mr-2" />
                {t('menu.account.informations')}
              </p>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleGoToChangelogs} className="cursor-pointer group">
              <p className="flex items-center child">
                <MegaphoneIcon className="mr-2" />
                {t('menu.account.changelogs')}
              </p>
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleLogout} className="cursor-pointer group">
              <p className="flex items-center child group-hover:text-destructive">
                <LogOutIcon className="mr-2" />
                {t('menu.admin.user.logout')}
              </p>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
